import React, { useState, type MouseEvent } from 'react';
import type { PortfolioFlag } from '@/features/dashboard/api';
import { FLAG_META, flagFixRoute, orderFlags } from '../flags';

interface Props {
  siteId: string;
  flags: PortfolioFlag[];
  language?: 'sv' | 'en';
}

/** Navigate to where a flag is fixed; stop the row's own click handler. */
function goToFix(e: MouseEvent, siteId: string, flag: PortfolioFlag) {
  e.stopPropagation();
  window.location.hash = flagFixRoute(siteId, flag);
}

interface ChipProps {
  siteId: string;
  flag: PortfolioFlag;
}

/** A single clickable severity chip. */
const Chip: React.FC<ChipProps> = ({ siteId, flag }) => {
  const meta = FLAG_META[flag];
  return (
    <button
      type="button"
      onClick={(e) => goToFix(e, siteId, flag)}
      className="cmd-label px-1 py-0.5 rounded text-[8px] transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-current"
      style={{ background: meta.bg, color: meta.color }}
      title={meta.description}
    >
      {meta.label}
    </button>
  );
};

/**
 * Per-site avvikelse indicator. The consistency rule, applied everywhere:
 *   0 flags  → render nothing (callers may show their own OK chip)
 *   1–2      → individual severity-ordered chips, each clickable to its fix
 *   ≥3       → one count pill in the worst flag's severity colour; hover/focus
 *              (or tap) opens a popover listing every flag as a clickable chip
 *              with a one-line SV description.
 */
export const AvvikelsePills: React.FC<Props> = ({ siteId, flags }) => {
  const [open, setOpen] = useState(false);
  const ordered = orderFlags(flags);

  if (ordered.length === 0) return null;

  if (ordered.length <= 2) {
    return (
      <div className="flex items-center justify-end gap-1 flex-wrap">
        {ordered.map((flag) => (
          <Chip key={flag} siteId={siteId} flag={flag} />
        ))}
      </div>
    );
  }

  // ≥3 flags → count pill + popover. Worst flag drives the pill's colour.
  const worst = FLAG_META[ordered[0]];

  return (
    <div
      className="relative inline-flex justify-end"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-expanded={open}
        className="cmd-label px-1.5 py-0.5 rounded text-[8px] inline-flex items-center gap-1 transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-current"
        style={{ background: worst.bg, color: worst.color }}
        title={`${ordered.length} avvikelser`}
      >
        <span className="tabular-nums">{ordered.length}</span>
        <span aria-hidden>⚠</span>
      </button>

      {open && (
        <div
          className="absolute z-20 right-0 top-full mt-1 rounded p-1.5 flex flex-col gap-1"
          style={{
            minWidth: 188,
            background: '#FFFFFF',
            border: '1px solid rgba(160,123,82,0.5)',
            boxShadow: '0 2px 8px rgba(7,51,49,0.10)',
          }}
        >
          {ordered.map((flag) => {
            const meta = FLAG_META[flag];
            return (
              <button
                key={flag}
                type="button"
                onClick={(e) => goToFix(e, siteId, flag)}
                className="flex items-center gap-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-[#073331]/[0.04] focus:outline-none focus-visible:bg-[#073331]/[0.04]"
              >
                <span
                  className="cmd-label px-1 py-0.5 rounded text-[8px] shrink-0"
                  style={{ background: meta.bg, color: meta.color }}
                >
                  {meta.label}
                </span>
                <span className="text-[10px] font-mono text-[#2E4640] whitespace-nowrap">
                  {meta.description}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
